<!-- Copyright (c) 2023, Alix Mougenot (Au pied des Arbres), alix.mougenot@gmail.com -->
<!-- See license file. -->
<script setup lang="ts">
import type { Ref, ComputedRef } from "vue";
import type {
  Actor,
  Dentist,
  Event,
  HealthCenter,
  OpenMode,
  EventCreation,
  CDOMember,
} from "@/types";
import {
  event,
  pureEventTypes,
  eventDirectionSelectOptions,
  eventTypeSelectOptions,
  diff,
} from "@/types";
import {
  actorsKey,
  attachDocumentKey,
  dentistKey,
  displayDocumentKey,
  healthCentersKey,
  membersKey,
  mutateEventKey,
  protagonistsKey,
} from "@/logic/provide";
import {
  actorsPathName,
  dentistsPathName,
  healthCentersPathName,
} from "@/router";
import { MDBTextarea } from "mdb-vue-ui-kit";
import { computed, ref, inject, watch, nextTick } from "vue";
import IconCommunication from "../icons/IconCommunication.vue";
import FormInput from "../FormInput.vue";
import { useRouter } from "vue-router";
import { findStakeholder } from "@/logic/search";
import FormSelect from "../FormSelect.vue";
import StakeholderSelector from "../actors/StakeholderSelector.vue";
import { useLoginStore } from "@/stores/login";

import { closeSecondaryViewKey, navigationKey } from "../navigation/navigation";
import { useToast } from "vue-toast-notification";

const notFoundLabel = "Non trouvé";
const notSetLabel = "Non défini";

const router = useRouter();
const props = defineProps<{ modelValue: Event; openMode: OpenMode }>();
const emit = defineEmits<{
  cancel: [];
  create: [element: EventCreation];
}>();

const actors = inject(actorsKey);
const dentists = inject(dentistKey);
const healthCenters = inject(healthCentersKey);
const protagonists = inject(protagonistsKey);
const members = inject(membersKey);
const editEvent = inject(mutateEventKey);

const closeSecondaryView = inject(closeSecondaryViewKey);
const currentNavKey = inject(navigationKey);
const attachDocument = inject(attachDocumentKey);
const displayDocument = inject(displayDocumentKey);

const currentUserStore = useLoginStore();
const toast = useToast();

const eventDiv = ref(null) as Ref<HTMLDivElement | null>;
const isEditing = ref(props.openMode === "Edit" || props.openMode === "Create");
const protagonistSearchMode = ref(false);
const model: Ref<Event> = ref(event(props.modelValue));

const isSelectingDocument = ref(false);
const panelX = ref(40); // placement of the event panel in the view

function toggleEdit() {
  isEditing.value = !isEditing.value;
  if (isEditing.value && eventDiv) {
    setTimeout(() => {
      eventDiv.value?.scrollIntoView({ behavior: "auto", block: "center" });
      showAssociatedDocument();
    }, 100);
  }
}

function cancel() {
  model.value = event(props.modelValue);
  isEditing.value = false;
  nextTick(() => {
    // We need the component to exist for the watcher to trigger and emit is synchronous as of summer 2024
    emit("cancel");
  });
}

watch(isEditing, () => {
  // Close any left behing template chosing window
  if (closeSecondaryView && currentNavKey) {
    closeSecondaryView(currentNavKey);
  }
});

function onSave(options?: { close: boolean }) {
  switch (props.openMode) {
    case "Create": {
      isEditing.value = options?.close === false;
      var toSave = { ...model.value } as any;
      delete toSave.id;
      delete toSave.creation_date;
      emit("create", toSave as EventCreation);
      model.value = event(props.modelValue);
      break;
    }
    case "Edit":
    case "InlineList": {
      if (editEvent) {
        const patch = diff(props.modelValue, model.value);
        if (Object.keys(patch).length > 0) {
          if (patch.document) {
            // making sure we update the entire document, this cannot be partial.
            patch.document = model.value.document;
          }
          editEvent(props.modelValue.id, patch).catch(() => {
            console.error("Failed to save event");
          });
        }
      } else {
        console.error("No edit Event function provided");
      }
      break;
    }
  }

  const leaveEditorOpen = options?.close === false;
  isEditing.value = false || leaveEditorOpen;
  isSelectingDocument.value = false || leaveEditorOpen;
}

const isIncomming = computed(
  () => model.value?.type && model.value.direction == "Entrant"
);

const isWithoutAnyExchange = computed(() =>
  pureEventTypes.includes(model.value.type)
);

const statusClass = computed(() => {
  let result = {
    warn: false,
    edit: isEditing.value,
  };
  if (!model.value.type) {
    result.warn = true;
  }
  return result;
});

const creationDate = computed(() => {
  if (!model.value.creation_date) {
    return "";
  }
  const d = Date.parse(model.value.creation_date);
  if (!d) {
    return "";
  }

  const date = new Date(d);
  return date.toLocaleDateString();
});

const receptionDate = computed(() => {
  if (!model.value.document_receipt_date) {
    return "";
  }
  const d = Date.parse(model.value.document_receipt_date);
  if (!d) {
    return "";
  }

  const date = new Date(d);
  return date.toLocaleDateString();
});

const receptionLabel = computed(() => {
  if (receptionDate.value) {
    return (
      "Reçu le " + receptionDate.value + ", enregistré le " + creationDate.value
    );
  } else {
    return "Date de réception inconnue, enregistré le " + creationDate.value;
  }
});

function findActor(id: string): {
  entity: Actor | Dentist | HealthCenter | null;
  error: string | null;
} {
  if (!dentists || !actors || !healthCenters) {
    return {
      entity: null,
      error: "...",
    };
  }

  let found = findStakeholder(
    [{ id, name: "", type: null }],
    actors,
    dentists,
    healthCenters
  );

  if (found && found?.found.length > 0) {
    return {
      entity: found.found[0],
      error: null,
    };
  }

  return {
    entity: null,
    error: notFoundLabel,
  };
}

const member: ComputedRef<{
  entity: Actor | Dentist | HealthCenter | CDOMember | null;
  error: string | null;
}> = computed(() => {
  if (!model.value.member_id) {
    return {
      error: notSetLabel,
      entity: null,
    };
  }

  if (members && model.value.member_id.startsWith("member:")) {
    const m = members.value.index.get(model.value.member_id);
    return {
      error: !m ? notFoundLabel : null,
      entity: m ? m : null,
    };
  }

  return findActor(model.value.member_id);
});

const memberLabel = computed(() => {
  if (member.value.error || !member.value.entity) {
    return member.value.error || notSetLabel;
  } else if ("name" in member.value.entity && member.value.entity.name) {
    // institution
    return member.value.entity.name;
  } else if ("display_name" in member.value.entity) {
    // member
    return member.value.entity.display_name;
  } else if ("first_name" in member.value.entity) {
    // actor or dentist
    return (
      member.value.entity?.first_name + " " + member.value.entity?.last_name
    );
  } else {
    return notSetLabel;
  }
});

const memberOptions = computed(() => {
  const options = new Array<{ value: string; text: string }>();
  const currentMember = currentUserStore.currentUser;
  if (model.value.member_id && model.value.member_id !== currentMember?.id) {
    options.push({
      value: model.value.member_id,
      text: memberLabel.value || notFoundLabel,
    });
  }
  if (currentMember && currentMember.id) {
    options.push({
      value: currentMember.id,
      text: currentMember.name,
    });
  }
  if (members) {
    members.value.options.forEach((m) => {
      if (m.value !== currentMember?.id) options.push(m);
    });
  }
  return options;
});

// Target handling
const protagonistIds = computed(() => {
  if (
    !protagonists ||
    !protagonists.value.accused ||
    !protagonists.value.plaignants
  ) {
    return [];
  }

  return protagonists.value.plaignants.found
    .map((a) => a.id)
    .concat(
      protagonists.value.plaignants.notFound
        .filter((a) => a !== null)
        .map((a) => a.id) as string[]
    )
    .concat(protagonists.value.accused.found.map((a) => a.id))
    .concat(
      protagonists.value.accused.notFound
        .filter((a) => a !== null)
        .map((a) => a.id) as string[]
    );
});

function setProtagonist(actor: Actor | Dentist | HealthCenter) {
  model.value.actor_id = actor.id;
  protagonistSearchMode.value = false;
}

const target: ComputedRef<{
  entity: Actor | Dentist | HealthCenter | null;
  error: string | null;
}> = computed(() => {
  if (!model.value.actor_id) {
    return {
      error: notSetLabel,
      entity: null,
    };
  }

  return findActor(model.value.actor_id);
});

const targetLabel = computed(() => {
  if (target.value.error || !target.value.entity) {
    return target.value.error || notSetLabel;
  } else if ("name" in target.value.entity && target.value.entity.name) {
    // institution
    return target.value.entity.name;
  } else if ("first_name" in target.value.entity) {
    // actor or dentist
    return (
      target.value.entity?.first_name + " " + target.value.entity?.last_name
    );
  } else {
    return notSetLabel;
  }
});

function navigateToTarget() {
  if (target.value.entity) {
    const path = target.value.entity.id.startsWith("pract:")
      ? dentistsPathName
      : "type" in target.value.entity &&
        target.value.entity.type === "Centre de Santé"
      ? healthCentersPathName
      : actorsPathName;

    router.push({ name: path, params: { id: target.value.entity.id } });
  }
}

function copyAssociatedEmail() {
  if (target.value.entity?.contact.email) {
    navigator.clipboard.writeText(target.value.entity.contact.email);
    toast.success("Email copié dans le presse-papier", {
      duration: 2000,
      position: "top-right",
    });
  }
}

function copyAssociatedDocumentContent() {
  if (model.value.document?.html_content) {
    const htmlBlob = new Blob([model.value.document?.html_content], {
      type: "text/html",
    });
    const textBlob = new Blob([model.value.document?.html_content], {
      type: "text/plain",
    });
    const item = new ClipboardItem({
      "text/html": htmlBlob,
      "text/plain": textBlob,
    });

    navigator.clipboard
      .write([item])
      .then(() => {
        toast.success("Contenu copié dans le presse-papier", {
          duration: 2000,
          position: "top-right",
        });
      })
      .catch((e) => {
        console.error(e);
        toast.error("Impossible de copier le contenu", {
          duration: 2000,
          position: "top-right",
        });
      });
  }
}

// Document selection handling
const recomputePanelX = () => {
  if (eventDiv.value) {
    var accu = eventDiv.value.offsetTop;
    var current = eventDiv.value;

    while (
      current &&
      !current.parentElement?.classList.contains("container-panel")
    ) {
      current = current.offsetParent as HTMLDivElement;
      accu += current.offsetTop;
    }
    panelX.value = accu;
  }
};

const canAttachDocument = computed(() => {
  return (
    model.value.type !== "Appel Téléphonique" &&
    model.value.type !== "Message sur Répondeur" &&
    model.value.direction !== "Entrant"
  );
});

const selectDocument = () => {
  if (!attachDocument) {
    console.error("No document attachment function provided");
    return;
  }
  isSelectingDocument.value = true;
  recomputePanelX();
  // Promise call to get the document after it is selected and edited
  attachDocument(model.value, panelX.value).then((document) => {
    if (document) {
      model.value.document = document;
      onSave({ close: false });
    }
    isSelectingDocument.value = false;
    setTimeout(() => {
      eventDiv.value?.scrollIntoView({ behavior: "auto", block: "center" });
    }, 100);
  });
};

function showAssociatedDocument(): void {
  if (!displayDocument) {
    console.error("No document attachment function provided");
    return;
  }
  const wasOpen = isEditing.value;
  if (model.value.document?.html_content) {
    recomputePanelX();
    displayDocument(model.value.document, model.value, panelX.value).then(
      (document) => {
        if (document) {
          model.value.document = document;
          onSave({ close: !wasOpen });
        }
        isSelectingDocument.value = false;
      }
    );
  }
}

const unselectDocument = () => {
  model.value.document = { title: "", html_content: "" };
  isSelectingDocument.value = false;
  onSave({ close: false });
};
</script>

<template>
  <div class="event" ref="eventDiv" :class="statusClass">
    <!-- LEFT -->
    <div class="event-intro">
      <div class="icons">
        <IconCommunication
          class="icon"
          :type="model.type"
          :direction="model.direction"
        />
        <img
          src="@/assets/icons/select.svg"
          alt="type of communication"
          class="arrow"
          :class="{ rotate: isIncomming }"
        />
      </div>
      <div class="date">{{ receptionDate ? receptionDate : creationDate }}</div>
    </div>
    <!-- HEADER -->
    <div class="event-content" :class="statusClass">
      <div class="header-container">
        <div class="d-header">
          <div class="minime">
            <h3>{{ model.type }}</h3>
          </div>
          <template v-if="!isWithoutAnyExchange">
            <template v-if="isIncomming">
              <span class="fakelink" @click="navigateToTarget">{{
                targetLabel
              }}</span>
              <img
                src="@/assets/icons/select.svg"
                alt="type of communication"
                class="arrow"
              />
              <div>
                {{ memberLabel }}
              </div>
            </template>
            <template v-else>
              <div>
                {{ memberLabel }}
              </div>
              <img
                src="@/assets/icons/select.svg"
                alt="type of communication"
                class="arrow"
              />
              <span class="fakelink" @click.stop="navigateToTarget">{{
                targetLabel
              }}</span>
            </template>
          </template>
        </div>
      </div>
      <!-- Main content -->
      <div class="content-container">
        <template v-if="isEditing">
          <div>
            <div class="split">
              <div>
                <FormSelect
                  label="Type"
                  v-model="model.type"
                  :options="eventTypeSelectOptions"
                />
              </div>
              <div>
                <FormSelect
                  label="Direction"
                  v-model="model.direction"
                  :options="eventDirectionSelectOptions"
                />
              </div>
            </div>
          </div>
          <div class="split">
            <div v-if="isEditing" class="grow">
              <FormSelect
                label="Conseiller en charche"
                v-model="model.member_id"
                :options="memberOptions"
              />
            </div>
            <div v-if="model.direction === 'Entrant'">
              <FormInput
                label="Reçu le"
                type="date"
                v-model="model.document_receipt_date"
              />
            </div>
          </div>
          <div class="line">
            <div class="grow">
              <FormInput
                label="Intéressé"
                type="text"
                v-model="targetLabel"
                :disabled="isEditing"
              />
            </div>
            <div
              v-if="!protagonistSearchMode"
              class="button right"
              @click="protagonistSearchMode = true"
            >
              Choisir
            </div>
          </div>
          <div v-if="protagonistSearchMode" class="searchProtagonist">
            <StakeholderSelector
              :label="null"
              cta="Choisir"
              :include-actors="true"
              :include-health-centers="true"
              :fast-selection="protagonistIds"
              @selected="setProtagonist"
              @closed="protagonistSearchMode = false"
            />
          </div>
          <div class="line" v-if="model.actor_id && canAttachDocument">
            <div class="grow">
              <FormInput
                label="Document"
                type="text"
                :modelValue="model.document?.title || 'Aucun document'"
                :disabled="true"
              />
            </div>
            <div class="buttons">
              <div
                class="button"
                @click="selectDocument"
                v-if="!model.document?.html_content"
              >
                Ajouter
              </div>
              <div
                v-if="model.document?.html_content"
                class="button"
                @click="unselectDocument"
              >
                Supprimer
              </div>
            </div>
          </div>
          <div>
            <MDBTextarea
              label="Notes"
              v-model="model.notes"
              :disabled="false"
            />
          </div>
        </template>
        <div v-else class="infoContainer">
          <div v-if="model.direction === 'Entrant'" class="flex gap">
            <h4>Date:&nbsp;</h4>
            <span>{{ receptionLabel }}</span>
          </div>
          <div v-if="model.document" class="flex gap">
            <h4>Document:&nbsp;</h4>
            <span class="fakelink" @click="showAssociatedDocument">{{
              model.document?.title
            }}</span>
          </div>
          <div class="flex gap">
            <h4 v-if="model.document">Raccourcis:&nbsp;</h4>
            <div v-if="model.type === 'Email'">
              <span
                v-if="target.entity?.contact.email"
                class="fakelink"
                @click="copyAssociatedEmail"
                >Copier l'email du destinataire</span
              ><span v-else>Le destinataire n'a pas d'email</span>
            </div>
            <div v-if="model.document">
              <span class="fakelink" @click="copyAssociatedDocumentContent">
                Copier le contenu du document
              </span>
            </div>
          </div>
          <div class="flex" v-if="model.notes">
            <MDBTextarea label="Notes" v-model="model.notes" :disabled="true" />
          </div>
        </div>
        <template v-if="isEditing">
          <div class="fill">
            <div class="buttons">
              <span class="button colored" @click.stop="cancel()">Annuler</span>
              <span class="button colored" @click.stop="onSave()"
                >Enregistrer</span
              >
            </div>
          </div>
        </template>
        <template v-else-if="openMode != 'ReadOnly'">
          <div class="fill">
            <div>
              <span class="button" @click.stop="toggleEdit()">Editer</span>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
img.select {
  transform: rotate(90deg) translateX(0.45em);
  padding-left: 0.25em;
  padding-right: 0.25em;
}

img.select {
  transform: rotate(90deg) translateX(0.45em);
  padding-left: 0.25em;
  padding-right: 0.25em;

  &.small {
    transform: rotate(90deg) translateX(0.45em) scale(0.8);
  }
}

.flex {
  display: flex;
  flex-direction: row;
  align-items: baseline;

  &.gap {
    gap: 1em;
  }
}

.meta {
  margin-left: 1em;
  margin-top: 1em;
}

.event {
  display: flex;
  flex-direction: row;
  position: relative;
  min-height: 40px;
  width: 100%;

  transition: all 0.1s ease-in-out;

  .event-intro {
    display: flex;
    flex-direction: column;
    padding-top: 15px;
    width: 80px;
    row-gap: 0.75em;

    font-style: normal;
    font-weight: 500;
    font-size: 13px;
    line-height: 16px;

    .icons {
      display: flex;
      flex-direction: row;
      align-items: flex-start;
      .icon {
        height: 30px;
        width: 30px;
      }

      .arrow {
        padding: 10px 10px 10px 4px;
      }

      .rotate {
        transform: rotate(180deg);
      }
    }
  }

  .event-content {
    display: flex;
    flex-direction: column;
    position: relative;
    width: calc(100% - 95px);
    background-color: var(--color-background-soft);
    border-radius: 6px;

    h2,
    h3,
    .colored {
      display: inline;
      color: var(--vt-c-purple);
      &.no-color {
        color: inherit;
      }
    }

    h4 {
      display: inline;
      color: var(--vt-c-purple);
      font-style: normal;
      font-weight: 400;
      font-size: 14px;
      line-height: 16px;
      margin-bottom: 1em;
      margin-left: 0.3em;
    }

    .infoContainer {
      display: flex;
      flex-direction: column;
    }
  }

  .header-container {
    display: flex;
    flex-direction: row;
    align-items: center;
    height: 56px;

    .d-header {
      display: flex;
      flex-direction: row;
      column-gap: 1em;
      align-items: baseline;
      text-wrap: nowrap;

      width: 100%;
      margin-left: 1em;
      margin-right: 1em;
    }

    .spaceme {
      margin-right: 1em;
    }
  }

  .fakelink {
    text-decoration: underline;
    cursor: pointer;
  }

  .content-container {
    display: flex;
    flex-direction: column;
    width: 100%;
    padding: 1em 1em;
    row-gap: 1.3em;

    .notes {
      display: block;
      margin-left: 0.5em;
    }
  }

  .buttons {
    span {
      margin-right: 1em;
    }
  }

  .split {
    display: flex;
    flex-direction: row;
    align-items: flex-end;
    justify-content: space-between;
    column-gap: 1.5em;

    div {
      width: 100%;
    }
  }

  .line {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: baseline;
    column-gap: 1.5em;
    width: 100%;

    .grow {
      flex-grow: 1;
    }

    .right {
      margin-left: auto;
      margin-right: 0.8em; // 2x 0.4
    }

    .buttons {
      display: flex;
      flex-direction: row;

      span,
      .button {
        margin-right: 1em;
      }
    }
  }

  .fill {
    width: 100%;
    text-align: right;
  }
}
</style>
